import React, { useState } from "react";
import { ArrowLeft, Check, Clock3 } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { syncPublicUserProfile } from "../../lib/userProfile";
import ProfileAvatar from "./ProfileAvatar";

const NAME_CHANGE_LOCK_KEY = "echo_jham_web_name_last_changed_at";
const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;

const getLastNameChange = () => Number(localStorage.getItem(NAME_CHANGE_LOCK_KEY) || 0);

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" });
};

const ChangeNamePanel = ({ userId, userEmail, currentName, onBack, onNameUpdated }) => {
  const [newName, setNewName] = useState(currentName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const lastChangeDate = getLastNameChange();
  const lockActive = lastChangeDate && Date.now() - lastChangeDate < SIX_MONTHS_MS;
  const remainingMs = lockActive ? SIX_MONTHS_MS - (Date.now() - lastChangeDate) : 0;
  const remainingDays = lockActive ? Math.max(1, Math.ceil(remainingMs / (24 * 60 * 60 * 1000))) : 0;

  const handleSave = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setError("Name is required.");
      setMessage("");
      return;
    }

    if (lockActive) {
      setError("You can only change your name once every 6 months.");
      setMessage("");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { full_name: trimmedName },
      });
      if (updateError) throw updateError;

      const {
        data: { user: updatedUser },
      } = await supabase.auth.getUser();

      await syncPublicUserProfile(supabase, {
        id: updatedUser?.id || userId,
        email: updatedUser?.email || userEmail,
        full_name: trimmedName,
      });

      localStorage.setItem(NAME_CHANGE_LOCK_KEY, String(Date.now()));
      setMessage("Name updated successfully.");
      onNameUpdated?.(trimmedName);
      onBack();
    } catch (updateErr) {
      setError(updateErr.message || "Unable to update name.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full min-w-0 text-left">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm font-medium text-white/80 transition hover:text-white"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      <h2 className="mt-4 text-2xl font-semibold tracking-tight text-white">Change Name</h2>

      <div className="mt-5 flex items-center gap-4 sm:gap-5">
        <ProfileAvatar />
        <div className="min-w-0">
          <h3 className="text-xl font-semibold leading-tight tracking-tight text-white break-words">
            {currentName || "Your Profile"}
          </h3>
          <p className="mt-1 text-sm text-white/55 break-words">{userEmail || "No email available"}</p>
          <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/70">
            <Clock3 size={12} />
            You can only change your name once every 6 months.
          </p>
        </div>
      </div>

      {lockActive ? (
        <p className="mt-4 text-sm text-amber-200">
          Last changed on {formatDate(lastChangeDate)}. You can try again in about {remainingDays} day(s).
        </p>
      ) : (
        <p className="mt-4 text-sm text-gray-400">Edit your display name below and save it once.</p>
      )}

      <div className="mt-5 rounded-xl border border-white/10 bg-[#1a1a1a] p-4 sm:p-5">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-gray-400">
          Name
        </label>
        <input
          type="text"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          disabled={lockActive}
          className="w-full rounded-md border border-white/10 bg-[#121212] px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-maroon disabled:cursor-not-allowed disabled:opacity-60"
          placeholder="Enter your name"
        />

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || lockActive}
          className="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded px-6 py-2.5 text-sm font-semibold text-white bg-brand-maroon hover:bg-red-700 disabled:opacity-60"
        >
          <Check size={16} />
          {saving ? "Saving..." : "Save Name"}
        </button>
      </div>

      {message ? <p className="mt-4 text-sm text-emerald-400">{message}</p> : null}
      {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}
    </div>
  );
};

export default ChangeNamePanel;
